import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { useUserData } from '../hooks/useUserData';
import { formatUSDT } from '../utils/format';
import { Spinner, PageLoader } from '../components/common/Loader';
import NetworkGuard from '../components/common/NetworkGuard';
import UserHeader from '../components/dashboard/UserHeader';
import MiningCounter from '../components/dashboard/MiningCounter';
import ReferralCard from '../components/dashboard/ReferralCard';
import WithdrawCTA from '../components/dashboard/WithdrawCTA';
import IncomeBreakdown from '../components/dashboard/IncomeBreakdown';
import PoolRewardsCard from '../components/dashboard/PoolRewardsCard';
import PaginatedActivity from '../components/dashboard/PaginatedActivity';
import StatsCard from '../components/dashboard/StatsCard';
import '../components/dashboard/Dashboard.css';
import '../components/dashboard/DashboardCards.css';

export default function DashboardPage() {
  const { isConnected, account, connect, connecting } = useWallet();
  const { userId, user, packages, activities, totals, refresh, loading, isRegistered } = useUserData();
  const navigate = useNavigate();

  if (!isConnected) {
    return (
      <div className="container dashboard">
        <div className="empty-state">
          <div className="icon">🔒</div>
          <h3 style={{ marginBottom: 8 }}>Connect Your Wallet</h3>
          <p style={{ marginBottom: 24 }}>Connect a Web3 wallet to view your dashboard.</p>
          <button className="btn btn-primary btn-lg" onClick={connect} disabled={connecting}>
            {connecting ? <><Spinner /> Connecting</> : 'Connect Wallet'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <NetworkGuard>
      <div className="container dashboard">
        {loading && !user ? (
          <PageLoader label="LOADING DASHBOARD" sub="Fetching your mining data..." />
        ) : !isRegistered ? (
          <div className="empty-state">
            <div className="icon">✨</div>
            <h3 style={{ marginBottom: 8 }}>Welcome to X-Terra Mining</h3>
            <p style={{ marginBottom: 24 }}>You're not registered yet. Create your account and start mining.</p>
            <button className="btn btn-primary btn-lg" onClick={() => navigate('/register')}>
              Register Now →
            </button>
          </div>
        ) : (
          <>
            <UserHeader
              userId={userId}
              user={user}
              account={account}
              isActive={user.isActive}
              onRefresh={refresh}
              refreshing={loading}
            />

            <MiningCounter userId={userId} user={user} packages={packages} onDone={refresh} />

            <PoolRewardsCard userId={userId} onDone={refresh} />

            <ReferralCard userId={userId} directReferrals={user.directReferrals} />

            <WithdrawCTA pending={user.pendingBalanceUSDT} />

            <div className="stats-grid">
              <StatsCard
                label="Total Purchased"
                value={formatUSDT(user.totalInvestmentUSDT)}
                unit="USDT"
                sub={`${Number(totals.activePackageCount)} active machines`}
              />
              <StatsCard
                label="Available Balance"
                value={formatUSDT(user.pendingBalanceUSDT)}
                unit="USDT"
                sub="Ready to withdraw"
              />
              <StatsCard
                label="Total Withdrawn"
                value={formatUSDT(user.totalWithdrawnUSDT)}
                unit="USDT"
                sub="All-time paid out"
              />
            </div>

            <IncomeBreakdown
              mining={totals.totalMiningEarned}
              levelIncome={totals.totalLevelIncomeEarned}
            />

            <PaginatedActivity activities={activities} />
          </>
        )}
      </div>
    </NetworkGuard>
  );
}
